import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Paper,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { playlistsAPI } from '../services/api';

const PlaylistEditPage = () => {
  const [playlist, setPlaylist] = useState({
    name: '',
    description: '',
    is_default: false
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = id !== 'new';

  useEffect(() => {
    if (isEditing) {
      loadPlaylist();
    }
  }, [id, isEditing]);

  const loadPlaylist = async () => {
    try {
      setLoading(true);
      const response = await playlistsAPI.getById(id);
      setPlaylist(response.data);
      setError('');
    } catch (error) {
      console.error('Ошибка загрузки плейлиста:', error);
      setError('Ошибка загрузки плейлиста');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    const value = field === 'is_default' ? event.target.checked : event.target.value;
    setPlaylist(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!playlist.name.trim()) {
      setError('Название плейлиста обязательно');
      return;
    }

    try {
      setSaving(true);
      setError('');
      
      if (isEditing) {
        await playlistsAPI.update(id, playlist);
        setSuccess('Плейлист успешно обновлен');
      } else {
        await playlistsAPI.create(playlist);
        setSuccess('Плейлист успешно создан');
        // Переходим к списку плейлистов после создания
        setTimeout(() => navigate('/playlists'), 1500);
      }
    } catch (error) {
      console.error('Ошибка сохранения плейлиста:', error);
      setError('Ошибка сохранения плейлиста');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/playlists');
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
        >
          Назад
        </Button>
        <Typography variant="h4" component="h1">
          {isEditing ? 'Редактирование плейлиста' : 'Создание плейлиста'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box component="form" noValidate>
          <TextField
            fullWidth
            label="Название плейлиста"
            value={playlist.name}
            onChange={handleInputChange('name')}
            margin="normal"
            required
            error={!playlist.name.trim() && error}
            helperText={!playlist.name.trim() && error ? 'Название обязательно' : ''}
          />

          <TextField
            fullWidth
            label="Описание"
            value={playlist.description}
            onChange={handleInputChange('description')}
            margin="normal"
            multiline
            rows={3}
            placeholder="Краткое описание плейлиста (необязательно)"
          />

          <FormControlLabel
            control={
              <Switch
                checked={playlist.is_default}
                onChange={handleInputChange('is_default')}
                disabled={isEditing && playlist.is_default} // Нельзя убрать статус дефолтного
              />
            }
            label="Плейлист по умолчанию"
            sx={{ mt: 2, mb: 2 }}
          />

          {playlist.is_default && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Плейлист по умолчанию используется для приложений, у которых не указан конкретный плейлист.
            </Alert>
          )}

          <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={saving}
            >
              Отмена
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || !playlist.name.trim()}
            >
              {saving ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default PlaylistEditPage;
